import { createFeatureSelector, createSelector } from '@ngrx/store'
import { findLast as _findLast, last as _last } from 'lodash/fp'

import {
  ConversationOutcomeClassification,
  ConversationOutcomeType,
  ConversationThreadMessageState,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { ProcessPlatform } from '@flowx/designer/shared/entities/misconfig.entity'
import {
  selectAppId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { AgentName } from './ai-conversation.commons'
import { ContextDataKeys } from './ai-conversation.model'
import { ConversationScope } from './conversation.constants'
import { ConversationTypes } from './conversation.types'
import { isFinishedThreadState } from './conversation.utils'

import { conversationFeatureKey } from '../store/ai.constants'

// This file contains selectors that need to be imported by ai-template-config.selectors.ts
// They are extracted here to avoid circular dependency issues

const selectFeature = createFeatureSelector<ConversationTypes>(conversationFeatureKey)

const conversations = createSelector(selectFeature, (state) => state.conversations)
const selectedProcessId = createSelector(selectFeature, (state) => state.selectedProcessId)

const conversationProcessMap = createSelector(
  selectFeature,
  (state) => state.conversationProcessMap
)

const conversationMapKey = createSelector(selectFeature, (state) => state.conversationMapKey)

const conversationRouteScope = createSelector(
  selectWorkspaceId,
  selectAppId,
  (workspaceId, appId): ConversationScope => ({
    workspaceId: workspaceId ?? null,
    appId: appId ?? null,
  })
)

const activeConversation = createSelector(
  conversations,
  conversationMapKey,
  conversationProcessMap,
  (conversations, conversationMapKey, conversationProcessMap) =>
    conversations.find(
      (conversation) => conversation.id === conversationProcessMap[conversationMapKey]
    )
)

const defaultThreadId = createSelector(
  activeConversation,
  (activeConversation) => activeConversation?.defaultThreadId ?? null
)

const activeAgentThread = createSelector(
  activeConversation,
  defaultThreadId,
  (activeConversation, defaultThreadId) => {
    if (!activeConversation) {
      return null
    }

    // Find the most recent non-finished thread that is not the default thread
    // and either contains at least one message with an action where the agent is not ConnectedGraph
    // OR has a UI hinted action
    const agentThreads = activeConversation.threads.filter(
      (thread) =>
        thread.id !== defaultThreadId &&
        !isFinishedThreadState(thread.state) &&
        (thread.messages?.some((message) =>
          message.executionPlan?.actions?.some(
            (action) => action.agent?.agent && action.agent.agent !== AgentName.ConnectedGraph
          )
        ) ||
          thread.messages?.some((message) =>
            message.contextData?.items?.some((item) => item.key === ContextDataKeys.UiAgent)
          ))
    )

    // Return the last (most recent) agent thread
    return _last(agentThreads) ?? null
  }
)

const activeAgentTarget = createSelector(activeAgentThread, (activeAgentThread) =>
  activeAgentThread?.messages
    ? ((
        _findLast(
          (message) => message.executionPlan?.actions[0]?.target?.targetType != null,
          activeAgentThread.messages
        ) ?? null
      )?.executionPlan?.actions[0]?.target ?? null)
    : null
)

// These are the selectors needed by ai-template-config.selectors.ts
export const userTaskNavigationAreaId = createSelector(activeAgentTarget, (activeAgentTarget) =>
  activeAgentTarget?.node?.navigationAreaId
    ? parseInt(activeAgentTarget?.node?.navigationAreaId, 10)
    : null
)

export const selectedTemplateConfigId = createSelector(activeAgentTarget, (activeAgentTarget) =>
  activeAgentTarget?.node?.templateConfigId
    ? parseInt(activeAgentTarget?.node?.templateConfigId, 10)
    : null
)

export type DispatchedUiFlowResource = {
  resourceId?: string
  platform?: ProcessPlatform
  templateConfigId?: number
}

// The UI_FLOW_RESOURCE context item the thread was dispatched with: the flow, the platform the
// generation was asked for, and — on scoped edits only — the edited area.
export const dispatchedUiFlowResource = createSelector(
  activeAgentThread,
  (activeAgentThread): DispatchedUiFlowResource | null => {
    const raw = activeAgentThread?.messages
      ?.flatMap((message) => message.contextData?.items ?? [])
      .find((item) => item.key === ContextDataKeys.UiFlowResource)?.valueGenericString

    if (!raw) {
      return null
    }

    try {
      const { resourceId, platform, templateConfigId } = JSON.parse(raw)
      return {
        resourceId,
        platform,
        ...(templateConfigId != null ? { templateConfigId: Number(templateConfigId) } : {}),
      }
    } catch (error) {
      console.error('Failed to parse UI_FLOW_RESOURCE context:', error)
      return null
    }
  }
)

// Scoped ui-flow edits carry the edited area's template id; whole-flow generations omit it.
export const selectedUiFlowScopedAreaId = createSelector(
  dispatchedUiFlowResource,
  (resource) => resource?.templateConfigId ?? null
)

export const selectedUiFlowContainerId = createSelector(
  dispatchedUiFlowResource,
  activeAgentTarget,
  (resource, activeAgentTarget) =>
    resource?.templateConfigId ?? (Number(activeAgentTarget?.uiFlow?.templateConfigId) || null)
)

// Reusable UI template targets carry no `node`, so the container the generation
// was launched from lives in the UI_TEMPLATE_RESOURCE context item instead.
export const selectedUiTemplateContainerId = createSelector(
  activeAgentThread,
  (activeAgentThread) => {
    const messageWithResource = _findLast(
      (message) =>
        !!message.contextData?.items?.some(
          (item) => item.key === ContextDataKeys.UiTemplateResource
        ),
      activeAgentThread?.messages ?? []
    )

    const raw = messageWithResource?.contextData?.items?.find(
      (item) => item.key === ContextDataKeys.UiTemplateResource
    )?.valueGenericString

    if (!raw) {
      return null
    }

    try {
      const { templateConfigId } = JSON.parse(raw)
      return templateConfigId ? parseInt(templateConfigId, 10) : null
    } catch (error) {
      console.error('Failed to parse UI_TEMPLATE_RESOURCE context:', error)
      return null
    }
  }
)

const loading = createSelector(selectFeature, (state) => state.conversationStatus === 'streaming')

// Returns all main outcomes (not just the first one)
const mainOutcomes = createSelector(activeAgentThread, (activeAgentThread) => {
  if (!activeAgentThread) {
    return []
  }

  return (
    activeAgentThread.outcome?.outcomes.filter(
      (outcome) =>
        outcome.outcomeType === ConversationOutcomeType.Textual &&
        outcome.outcomeClassification === ConversationOutcomeClassification.Main
    ) ?? []
  )
})

// Returns the content of the first main outcome (for backward compatibility)
const mainOutcome = createSelector(
  mainOutcomes,
  (mainOutcomes) => mainOutcomes[0]?.textual?.content ?? null
)

const isProcessingOrHasDraft = createSelector(
  loading,
  activeAgentThread,
  mainOutcome,
  (loading, activeAgentThread, mainOutcome) =>
    loading ||
    activeAgentThread?.messages.some(
      (message) => message.state === ConversationThreadMessageState.Processing
    ) ||
    mainOutcome !== null
)

// Export internal selectors for use in conversation.selectors.ts
export {
  selectFeature,
  conversations,
  selectedProcessId,
  conversationProcessMap,
  conversationMapKey,
  conversationRouteScope,
  activeConversation,
  defaultThreadId,
  activeAgentThread,
  activeAgentTarget,
  loading,
  mainOutcomes,
  mainOutcome,
  isProcessingOrHasDraft,
}
